import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Hammer, Eye, EyeOff, CheckCircle, AlertCircle,
  Building2, Mail, MapPin, Briefcase, ChevronDown, Globe,
  Paperclip, Upload, X, Check, FileText, Settings
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageToggle from '../components/LanguageToggle';
import { PhoneInput } from '../components/PhoneInput';
import { UN_COUNTRIES_EN } from '../countries';
import FieldError from '../components/FieldError';
import { isValidEmail, isValidPhoneDigits } from '../lib/validation';
import { checkPasswordRules } from '../lib/passwordPolicy';
import FilePreviewCarousel from '../components/FilePreviewCarousel';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Phase 86AZ.24 — Contractor doc constraints (mirror backend).
const MAX_FILES = 10;
const MAX_TOTAL_BYTES = 100 * 1024 * 1024; // 100 MB combined
const CHUNK_SIZE = 5 * 1024 * 1024;        // 5 MB per chunk

const PasswordRule = ({ ok, label }) => (
  <div className={`flex items-center gap-1.5 text-xs ${ok ? 'text-emerald-600' : 'text-gray-400'}`}>
    {ok ? <CheckCircle className="h-3 w-3" /> : <div className="h-3 w-3 rounded-full border border-gray-300" />}
    {label}
  </div>
);

const StepIndicator = ({ currentStep, totalSteps, stepTitles }) => (
  <div className="flex items-center justify-center gap-0 mb-8">
    {Array.from({ length: totalSteps }, (_, i) => {
      const stepNum = i + 1;
      const isDone = stepNum < currentStep;
      const isActive = stepNum === currentStep;
      return (
        <React.Fragment key={stepNum}>
          <div className="flex flex-col items-center">
            <div className={`h-9 w-9 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${
              isDone ? 'bg-orange-500 text-white shadow-md' :
              isActive ? 'bg-white text-orange-600 shadow-lg ring-2 ring-orange-400' :
              'bg-orange-900/40 text-orange-300/60'
            }`}>
              {isDone ? <Check className="h-4 w-4" /> : stepNum}
            </div>
            <span className={`mt-1.5 text-xs font-medium whitespace-nowrap ${
              isActive ? 'text-white' : isDone ? 'text-orange-300' : 'text-orange-400/50'
            }`}>{stepTitles[i]}</span>
          </div>
          {i < totalSteps - 1 && (
            <div className={`h-0.5 w-10 sm:w-16 mx-1 mt-[-18px] transition-all duration-300 ${
              stepNum < currentStep ? 'bg-orange-400' : 'bg-orange-800/40'
            }`} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

function SummaryRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="text-gray-500 min-w-[80px]">{label}:</span>
      <span className="font-semibold text-gray-800 truncate">{value}</span>
    </div>
  );
}

export default function ContractorRegister() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const tr = language === 'tr';
  const fileInputRef = useRef(null);

  const [step, setStep] = useState(1);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    contractor_category_id: '', contractor_category_name: '',
    full_name: '', company: '', title: '',
    phone_code: '+90', phone_number: '', country: '', city: '',
    website: '', about_us: '',
    email: '', password: '', confirm_password: '',
    wantsAttachment: false,
  });
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  // Phase 86AZ.24 — Preview carousel (PDF.js + image) state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  // Per-file upload progress (% 0–100) keyed by file.name+size
  const [uploadProgress, setUploadProgress] = useState({});
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passTouched, setPassTouched] = useState(false);
  const [confirmTouched, setConfirmTouched] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  // Phase 86AZ.51 — Exit-confirm modal so accidental Back/swipe-touchpad
  // gestures don't wipe a half-filled form.
  const [confirmExitOpen, setConfirmExitOpen] = useState(false);

  const passwordRules = checkPasswordRules(form.password);
  const allRulesOk = Object.values(passwordRules).every(Boolean);
  const passwordsMatch = form.password === form.confirm_password;
  const emailValid  = !form.email || isValidEmail(form.email);
  const phoneValid  = !form.phone_number || isValidPhoneDigits(form.phone_number, { min: 7, max: 15 });
  const [fieldErrors, setFieldErrors] = useState({});
  const clearFieldError = (k) => setFieldErrors(prev => { if (!prev[k]) return prev; const n = {...prev}; delete n[k]; return n; });

  // Phase 86AZ.49 — Category step removed; all contractors default to
  // "Fuar Stand Üreticisi" / "Exhibition Stand Contractor". Steps shrink to 3.
  const stepTitles = tr
    ? ['Firma Bilgileri', 'Hesap', 'Belgeler']
    : ['Company Info', 'Account', 'Documents'];

  useEffect(() => {
    // Auto-assign default category from backend (or fall back to a fixed one).
    axios.get(`${API}/auth/public/contractor-categories`)
      .then(r => {
        const list = r.data || [];
        setCategories(list);
        const first = list[0] || { id: 'default', name: 'Fuar Stand Üreticisi' };
        setForm(f => ({
          ...f,
          contractor_category_id: f.contractor_category_id || first.id,
          contractor_category_name: f.contractor_category_name || first.name,
        }));
      })
      .catch(() => {
        const fallback = { id: 'default', name: 'Fuar Stand Üreticisi' };
        setCategories([fallback]);
        setForm(f => ({
          ...f,
          contractor_category_id: f.contractor_category_id || fallback.id,
          contractor_category_name: f.contractor_category_name || fallback.name,
        }));
      });
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleCategoryChange = (e) => {
    const selected = categories.find(c => c.id === e.target.value);
    setForm(f => ({ ...f, contractor_category_id: e.target.value, contractor_category_name: selected?.name || '' }));
  };

  const validateStep = (s) => {
    setError('');
    const errs = {};
    const reqMsg = tr ? 'Bu alan zorunludur.' : 'This field is required.';
    if (s === 1) {
      if (!form.company.trim())   errs.company   = reqMsg;
      if (!form.full_name.trim()) errs.full_name = reqMsg;
      if (form.phone_number && !phoneValid) { errs.phone_number = tr ? 'Telefon 7-15 rakam içermelidir.' : 'Phone must be 7-15 digits.'; setPhoneTouched(true); }
    }
    if (s === 2) {
      if (!form.email.trim()) { errs.email = reqMsg; setEmailTouched(true); }
      else if (!isValidEmail(form.email)) { errs.email = tr ? 'Geçerli bir e-posta giriniz.' : 'Please enter a valid email.'; setEmailTouched(true); }
      if (!form.password) errs.password = reqMsg;
      else if (!allRulesOk) { errs.password = tr ? 'Şifre kurallarını karşılamıyor.' : 'Password does not meet requirements.'; setPassTouched(true); }
      if (!form.confirm_password) errs.confirm_password = reqMsg;
      else if (!passwordsMatch)  { errs.confirm_password = tr ? 'Şifreler uyuşmuyor.' : 'Passwords do not match.'; setConfirmTouched(true); }
    }
    setFieldErrors(errs);
    if (Object.keys(errs).length) {
      setError(tr ? 'Lütfen işaretli alanları kontrol ediniz.' : 'Please review the highlighted fields.');
      return false;
    }
    return true;
  };

  const nextStep = () => { if (!validateStep(step)) return; setStep(s => s + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const prevStep = () => { setError(''); setStep(s => s - 1); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  // Phase 86AZ.51 — Detect whether the user has started filling the form.
  // We deliberately ignore the auto-assigned category fields and the
  // default phone country code (+90). Anything else counts as "dirty".
  const isFormDirty = (() => {
    if (success) return false;
    const f = form;
    if (f.full_name?.trim()) return true;
    if (f.company?.trim()) return true;
    if (f.title?.trim()) return true;
    if (f.phone_number?.trim()) return true;
    if (f.country) return true;
    if (f.city?.trim()) return true;
    if (f.website?.trim()) return true;
    if (f.about_us?.trim()) return true;
    if (f.email?.trim()) return true;
    if (f.password) return true;
    if (f.confirm_password) return true;
    if (attachmentFiles.length > 0) return true;
    return false;
  })();

  const attemptExit = () => {
    if (isFormDirty) {
      setConfirmExitOpen(true);
    } else {
      navigate('/giris?tab=contractor');
    }
  };
  const confirmExit = () => {
    setConfirmExitOpen(false);
    // Pop our guard entry so navigate goes one step further back.
    navigate('/giris?tab=contractor');
  };

  // Phase 86AZ.51 — Intercept the browser/touchpad Back gesture. We push
  // a dummy history entry on mount; if the user gestures back, we catch
  // the popstate, re-push the guard, and open the confirm modal instead
  // of letting the form unmount.
  useEffect(() => {
    if (success) return;
    try {
      window.history.pushState({ contractorRegisterGuard: true }, '');
    } catch { /* ignore */ }
    const onPop = () => {
      if (isFormDirty) {
        try { window.history.pushState({ contractorRegisterGuard: true }, ''); } catch { /* ignore */ }
        setConfirmExitOpen(true);
      } else {
        navigate('/giris?tab=contractor');
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [isFormDirty, success]);

  // Native beforeunload prompt for tab close / hard refresh while dirty.
  useEffect(() => {
    if (!isFormDirty || success) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isFormDirty, success]);

  // Phase 86AZ.24 — Upload a single File in 5 MB chunks. Returns the
  // server-stored filename so it can be sent as part of the final
  // /contractor-register call. Progress (0–100) is reported via cb.
  const uploadFileChunked = async (file, onProgress) => {
    const uploadId = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const blob = file.slice(start, end);
      const fd = new FormData();
      fd.append('upload_id', uploadId);
      fd.append('chunk_index', String(i));
      fd.append('total_chunks', String(totalChunks));
      fd.append('filename', file.name);
      fd.append('chunk', blob, `${i}.part`);
      // Progress = (chunks done + this chunk's upload progress) / total
      await axios.post(`${API}/auth/contractor-register/upload-chunk`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (ev) => {
          const chunkPct = ev.total ? ev.loaded / ev.total : 1;
          const pct = Math.round(((i + chunkPct) / totalChunks) * 100);
          onProgress?.(Math.min(99, pct));
        },
      });
    }
    const fd2 = new FormData();
    fd2.append('upload_id', uploadId);
    fd2.append('total_chunks', String(totalChunks));
    fd2.append('filename', file.name);
    const fin = await axios.post(`${API}/auth/contractor-register/finalize-upload`, fd2, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    onProgress?.(100);
    return fin.data?.filename;
  };

  const handleSubmit = async () => {
    setError(''); setLoading(true);
    try {
      // 1) Pre-upload attachments via chunked endpoints
      let attachmentFilenamesCsv = '';
      if (form.wantsAttachment && attachmentFiles.length > 0) {
        const stored = [];
        for (let i = 0; i < attachmentFiles.length; i++) {
          const f = attachmentFiles[i];
          const key = `${f.name}::${f.size}`;
          setUploadProgress(p => ({ ...p, [key]: 0 }));
          const name = await uploadFileChunked(f, (pct) => {
            setUploadProgress(p => ({ ...p, [key]: pct }));
          });
          if (name) stored.push(name);
        }
        attachmentFilenamesCsv = stored.join(',');
      }

      // 2) Submit registration metadata (no large file payload anymore)
      const fd = new FormData();
      fd.append('full_name', form.full_name);
      fd.append('email', form.email);
      fd.append('company', form.company);
      fd.append('phone', form.phone_number ? `${form.phone_code} ${form.phone_number}` : '');
      fd.append('country', form.country);
      fd.append('city', form.city);
      fd.append('title', form.title);
      fd.append('contractor_category_id', form.contractor_category_id);
      fd.append('contractor_category_name', form.contractor_category_name);
      fd.append('website', form.website || '');
      fd.append('about_us', form.about_us || '');
      fd.append('password', form.password);
      fd.append('language', language);
      fd.append('is_individual', 'false');
      if (attachmentFilenamesCsv) fd.append('attachment_filenames', attachmentFilenamesCsv);
      await axios.post(`${API}/auth/contractor-register`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || (tr ? 'Bir hata oluştu.' : 'An error occurred.'));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-900 via-red-800 to-orange-900 flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 shrink-0">
          <button onClick={() => navigate('/giris?tab=contractor')}
            className="flex items-center gap-2 text-white/80 hover:text-white transition-colors text-sm font-medium">
            <ArrowLeft className="h-4 w-4" />
            {tr ? 'Giriş' : 'Login'}
          </button>
          <div className="text-white font-playfair text-2xl tracking-tight">SummitFlow</div>
          <LanguageToggle variant="admin" />
        </div>
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl p-10 max-w-md w-full text-center">
            <div className="h-20 w-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-5">
              <CheckCircle className="h-10 w-10 text-emerald-600" />
            </div>
            <h2 className="font-playfair text-2xl md:text-3xl text-[#0A3A5A] font-medium mb-3 tracking-tight">
              {tr ? 'Başvurunuz Alındı!' : 'Application Received!'}
            </h2>
            <div className="bg-orange-50 rounded-2xl p-4 mb-5 text-left space-y-2">
              <SummaryRow label={tr ? 'Firma' : 'Company'} value={form.company} />
              <SummaryRow label={tr ? 'Kategori' : 'Category'} value={form.contractor_category_name} />
              <SummaryRow label={tr ? 'E-posta' : 'Email'} value={form.email} />
            </div>
            <p className="text-gray-500 text-sm mb-6 leading-relaxed">
              {tr
                ? 'Başvurunuz Ultra Admin tarafından incelenecektir. Onay sonrasında giriş yapabileceksiniz.'
                : 'Your application will be reviewed by Ultra Admin. You will be able to login upon approval.'}
            </p>
            <Button data-testid="contractor-back-login" onClick={() => navigate('/giris?tab=contractor')} className="bg-orange-600 hover:bg-orange-700 text-white rounded-full px-8">
              {tr ? 'Giriş Sayfasına Dön' : 'Back to Login'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const inputCls = "rounded-xl border-gray-200 pl-10 focus:ring-orange-400";

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-900 via-red-800 to-orange-900 flex flex-col">
      <div className="flex items-center justify-between px-6 py-4 shrink-0">
        <button
          data-testid="contractor-register-top-back"
          onClick={() => step > 1 ? prevStep() : attemptExit()}
          className="flex items-center gap-2 text-white/80 hover:text-white transition-colors text-sm font-medium">
          <ArrowLeft className="h-4 w-4" />
          {step > 1 ? (tr ? 'Geri' : 'Back') : (tr ? 'Giriş' : 'Login')}
        </button>
        <div className="text-white font-playfair text-2xl tracking-tight">SummitFlow</div>
        <LanguageToggle variant="admin" />
      </div>

      <div className="flex-1 flex items-start justify-center px-4 pb-10 pt-2">
        <div className="w-full max-w-3xl">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-orange-500/20 backdrop-blur mb-3">
              <Hammer className="h-7 w-7 text-orange-300" />
            </div>
            <h1 className="font-playfair text-3xl md:text-4xl text-white font-medium mb-1 tracking-tight">
              {tr ? 'Yüklenici Başvurusu' : 'Contractor Application'}
            </h1>
            <p className="text-white/60 text-sm">
              {tr ? 'SummitFlow yüklenici platformuna başvurun' : 'Apply to the SummitFlow contractor platform'}
            </p>
          </div>

          <StepIndicator currentStep={step} totalSteps={3} stepTitles={stepTitles} />

          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            <div className="p-7 md:p-10 space-y-5">

              {/* ─── STEP 1: Company Info ─── */}
              {step === 1 && (
                <>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="h-8 w-8 rounded-xl bg-orange-100 flex items-center justify-center">
                      <Building2 className="h-4 w-4 text-orange-600" />
                    </div>
                    <div>
                      <h2 className="font-bold text-gray-800 text-base">{tr ? 'Firma Bilgileri' : 'Company Info'}</h2>
                      <p className="text-xs text-gray-400">{tr ? 'Firma ve iletişim bilgilerinizi girin.' : 'Enter company and contact details.'}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2 space-y-1.5">
                      <Label className="text-sm text-gray-700">{tr ? 'Firma Adı' : 'Company Name'} <span className="text-red-500">*</span></Label>
                      <div className="relative">
                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                        <Input data-testid="contractor-company" value={form.company}
                          onChange={e => { set('company', e.target.value); clearFieldError('company'); }}
                          className={`${inputCls} ${fieldErrors.company ? 'border-red-400' : ''}`} placeholder="ABC İnşaat A.Ş." />
                      </div>
                      <FieldError msg={fieldErrors.company} />
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-sm text-gray-700">{tr ? 'Ad Soyad' : 'Full Name'} <span className="text-red-500">*</span></Label>
                      <div className="relative">
                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                        <Input data-testid="contractor-fullname" value={form.full_name}
                          onChange={e => { set('full_name', e.target.value); clearFieldError('full_name'); }}
                          className={`${inputCls} ${fieldErrors.full_name ? 'border-red-400' : ''}`} placeholder="Ahmet Yılmaz" />
                      </div>
                      <FieldError msg={fieldErrors.full_name} />
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-sm text-gray-700">{tr ? 'Görev' : 'Title'}</Label>
                      <div className="relative">
                        <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                        <Input value={form.title} onChange={e => set('title', e.target.value)} className={inputCls} placeholder={tr ? 'Genel Müdür' : 'CEO'} />
                      </div>
                    </div>

                    <div className="col-span-2 space-y-1.5">
                      <Label className="text-sm text-gray-700">{tr ? 'Telefon' : 'Phone'}</Label>
                      <PhoneInput
                        codeValue={form.phone_code}
                        numberValue={form.phone_number}
                        onCodeChange={v => set('phone_code', v)}
                        onNumberChange={v => { set('phone_number', v); if (!phoneTouched && v) setPhoneTouched(true); }}
                      />
                      {phoneTouched && form.phone_number && !phoneValid && (
                        <p className="text-xs text-red-500 flex items-center gap-1 mt-1">
                          <AlertCircle className="h-3 w-3" />
                          {tr ? 'Telefon 7–15 rakam içermelidir (harf/özel karakter kabul edilmez)' : 'Phone must be 7–15 digits (no letters/symbols)'}
                        </p>
                      )}
                    </div>

                    <div className="col-span-2 space-y-1.5">
                      <Label className="text-sm text-gray-700">{tr ? 'Ülke' : 'Country'}</Label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none z-10" />
                        <select value={form.country} onChange={e => set('country', e.target.value)}
                          className="w-full pl-10 pr-8 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-300 appearance-none text-gray-700">
                          <option value="">{tr ? '-- Ülke --' : '-- Country --'}</option>
                          {UN_COUNTRIES_EN.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                      </div>
                    </div>

                    <div className="col-span-2 space-y-1.5">
                      <Label className="text-sm text-gray-700">{tr ? 'Şehir' : 'City'}</Label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                        <Input value={form.city} onChange={e => set('city', e.target.value)} className={inputCls} placeholder="İstanbul" />
                      </div>
                    </div>

                    {/* Phase 86AZ.49 — Website (optional) */}
                    <div className="col-span-2 space-y-1.5">
                      <Label className="text-sm text-gray-700">{tr ? 'Web Adresi' : 'Website'}</Label>
                      <div className="relative">
                        <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                        <Input
                          data-testid="contractor-website"
                          value={form.website}
                          onChange={e => set('website', e.target.value)}
                          className={inputCls}
                          placeholder="https://www.firmaniz.com"
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* ─── STEP 2: Account ─── */}
              {step === 2 && (
                <>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="h-8 w-8 rounded-xl bg-orange-100 flex items-center justify-center">
                      <Settings className="h-4 w-4 text-orange-600" />
                    </div>
                    <div>
                      <h2 className="font-bold text-gray-800 text-base">{tr ? 'Hesap Bilgileri' : 'Account Setup'}</h2>
                      <p className="text-xs text-gray-400">{tr ? 'E-posta ve şifre belirleyin.' : 'Set your login credentials.'}</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="space-y-1.5">
                      <Label className="text-sm text-gray-700">{tr ? 'E-posta' : 'Email'} <span className="text-red-500">*</span></Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                        <Input
                          data-testid="contractor-email"
                          type="email"
                          value={form.email}
                          onChange={e => { set('email', e.target.value.trim()); clearFieldError('email'); }}
                          onBlur={() => setEmailTouched(true)}
                          className={`${inputCls} ${(fieldErrors.email || (emailTouched && form.email && !emailValid)) ? 'border-red-400' : ''}`}
                          placeholder="ornek@firma.com"
                          autoComplete="email"
                        />
                      </div>
                      <FieldError
                        msg={fieldErrors.email || (emailTouched && form.email && !emailValid
                          ? (tr ? 'Geçerli bir e-posta giriniz (örn. ad@firma.com)' : 'Please enter a valid email (e.g. name@company.com)')
                          : null)}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-sm text-gray-700">{tr ? 'Şifre' : 'Password'} <span className="text-red-500">*</span></Label>
                      <div className="relative">
                        <Input data-testid="contractor-password" type={showPass ? 'text' : 'password'} value={form.password}
                          onChange={e => { set('password', e.target.value); clearFieldError('password'); }} onBlur={() => setPassTouched(true)}
                          className={`rounded-xl pr-10 ${(fieldErrors.password || (passTouched && !allRulesOk && form.password)) ? 'border-red-400' : 'border-gray-200'}`} placeholder="••••••••" />
                        <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                          {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      <FieldError msg={fieldErrors.password} />
                      {(passTouched || form.password.length > 0) && (
                        <div className="grid grid-cols-2 gap-1 pt-1">
                          <PasswordRule ok={passwordRules.length} label={tr ? 'En az 8 karakter' : 'Min 8 chars'} />
                          <PasswordRule ok={passwordRules.uppercase} label={tr ? 'Büyük harf' : 'Uppercase'} />
                          <PasswordRule ok={passwordRules.lowercase} label={tr ? 'Küçük harf' : 'Lowercase'} />
                          <PasswordRule ok={passwordRules.number} label={tr ? 'Rakam' : 'Number'} />
                          <PasswordRule ok={passwordRules.special} label={tr ? 'Özel karakter' : 'Special char'} />
                        </div>
                      )}
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-sm text-gray-700">{tr ? 'Şifre Tekrar' : 'Confirm Password'} <span className="text-red-500">*</span></Label>
                      <div className="relative">
                        <Input data-testid="contractor-confirm-password" type={showConfirm ? 'text' : 'password'} value={form.confirm_password}
                          onChange={e => { set('confirm_password', e.target.value); clearFieldError('confirm_password'); }} onBlur={() => setConfirmTouched(true)}
                          className={`rounded-xl pr-10 ${(fieldErrors.confirm_password || (confirmTouched && form.confirm_password && !passwordsMatch)) ? 'border-red-400' : confirmTouched && passwordsMatch && form.confirm_password ? 'border-emerald-400' : 'border-gray-200'}`} placeholder="••••••••" />
                        <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                          {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      <FieldError
                        msg={fieldErrors.confirm_password || (confirmTouched && form.confirm_password && !passwordsMatch
                          ? (tr ? 'Şifreler uyuşmuyor.' : 'Passwords do not match.')
                          : null)}
                      />
                    </div>
                  </div>
                </>
              )}

              {/* ─── STEP 3: Documents & Summary ─── */}
              {step === 3 && (
                <>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="h-8 w-8 rounded-xl bg-orange-100 flex items-center justify-center">
                      <FileText className="h-4 w-4 text-orange-600" />
                    </div>
                    <h2 className="font-bold text-gray-800 text-base">{tr ? 'Belgeler & Özet' : 'Documents & Review'}</h2>
                  </div>

                  <div className="bg-orange-50 border border-orange-200 rounded-2xl p-4 space-y-2">
                    <p className="text-xs font-semibold text-orange-700 uppercase tracking-wide mb-2">{tr ? 'Başvuru Özeti' : 'Summary'}</p>
                    <SummaryRow label={tr ? 'Firma' : 'Company'} value={form.company} />
                    <SummaryRow label={tr ? 'Ad Soyad' : 'Name'} value={form.full_name} />
                    <SummaryRow label={tr ? 'E-posta' : 'Email'} value={form.email} />
                    {form.website && <SummaryRow label={tr ? 'Web' : 'Website'} value={form.website} />}
                    {form.country && <SummaryRow label={tr ? 'Ülke' : 'Country'} value={form.country} />}
                  </div>

                  <label data-testid="contractor-attachment-label"
                    className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${form.wantsAttachment ? 'border-orange-400 bg-orange-50' : 'border-gray-200 hover:border-orange-200'}`}>
                    <div className="relative mt-0.5 flex-shrink-0">
                      <input data-testid="contractor-attachment-checkbox" type="checkbox" checked={form.wantsAttachment}
                        onChange={e => { set('wantsAttachment', e.target.checked); if (!e.target.checked) setAttachmentFiles([]); }} className="sr-only" />
                      <div className={`h-5 w-5 rounded border-2 flex items-center justify-center transition-all ${form.wantsAttachment ? 'bg-orange-500 border-orange-500' : 'border-gray-300 bg-white'}`}>
                        {form.wantsAttachment && <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-800">{tr ? 'Referans / Belge Ekle' : 'Attach References / Documents'}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {tr
                          ? 'PDF, Word, Excel, fotoğraf — portföy, sertifika veya tanıtım dosyası. (Maks. 10 dosya · toplam 100 MB)'
                          : 'PDF, Word, Excel, image — portfolio, certificate or profile. (Max 10 files · 100 MB total)'}
                      </p>
                    </div>
                  </label>

                  {form.wantsAttachment && (
                    <div className="space-y-2">
                      <input ref={fileInputRef} type="file" className="hidden"
                        multiple
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp,application/pdf"
                        onChange={e => {
                          // Phase 86AZ.24 — Multi-select: per-file ext check,
                          // total budget check (100 MB across all selected
                          // files), 10-file cap. Merge into existing list.
                          const picked = Array.from(e.target.files || []);
                          if (picked.length === 0) return;
                          const allowed = ['.pdf','.doc','.docx','.xls','.xlsx','.jpg','.jpeg','.png','.webp'];
                          const rejected = [];
                          const accepted = [];
                          let runningTotal = attachmentFiles.reduce((s, f) => s + f.size, 0);
                          picked.forEach(f => {
                            const ext = f.name.substring(f.name.lastIndexOf('.')).toLowerCase();
                            if (!allowed.includes(ext)) { rejected.push(`${f.name} (tür)`); return; }
                            if (runningTotal + f.size > MAX_TOTAL_BYTES) {
                              rejected.push(`${f.name} (${tr ? 'toplam' : 'total'} > 100MB)`);
                              return;
                            }
                            runningTotal += f.size;
                            accepted.push(f);
                          });
                          if (rejected.length) {
                            setError(tr
                              ? `Reddedildi: ${rejected.join(', ')}. Toplam ≤ 100 MB, sadece PDF/Word/Excel/Görsel.`
                              : `Rejected: ${rejected.join(', ')}. Total ≤ 100 MB, only PDF/Word/Excel/Image.`);
                          } else {
                            setError('');
                          }
                          setAttachmentFiles(prev => {
                            const merged = [...prev];
                            for (const f of accepted) {
                              if (merged.length >= MAX_FILES) break;
                              if (!merged.find(x => x.name === f.name && x.size === f.size)) {
                                merged.push(f);
                              }
                            }
                            return merged;
                          });
                          e.target.value = '';
                        }} />
                      {attachmentFiles.length > 0 && (
                        <div className="flex items-center justify-between text-[11px] text-gray-500 px-1">
                          <span>
                            {tr ? 'Toplam' : 'Total'}:&nbsp;
                            <span className="font-semibold text-gray-700">
                              {(attachmentFiles.reduce((s, f) => s + f.size, 0) / 1024 / 1024).toFixed(1)} MB
                            </span>
                            &nbsp;/&nbsp;100 MB · {attachmentFiles.length}/{MAX_FILES}
                          </span>
                          <button
                            type="button"
                            data-testid="contractor-preview-all-btn"
                            onClick={() => { setPreviewIndex(0); setPreviewOpen(true); }}
                            className="inline-flex items-center gap-1 text-orange-600 font-semibold hover:underline"
                          >
                            <Eye className="h-3.5 w-3.5" />
                            {tr ? 'Tümünü Önizle' : 'Preview All'}
                          </button>
                        </div>
                      )}
                      {attachmentFiles.map((f, idx) => {
                        const ext = f.name.substring(f.name.lastIndexOf('.') + 1).toLowerCase();
                        const isPdf = ext === 'pdf';
                        const isImg = ['jpg','jpeg','png','webp'].includes(ext);
                        const key = `${f.name}::${f.size}`;
                        const pct = uploadProgress[key];
                        return (
                          <div key={idx} className="px-3 py-2.5 bg-orange-50 border border-orange-200 rounded-xl">
                            <div className="flex items-center gap-2">
                              {isPdf
                                ? <span className="inline-flex items-center justify-center w-7 h-7 rounded bg-red-100 text-red-700 text-[10px] font-black flex-shrink-0">PDF</span>
                                : <Paperclip className="h-4 w-4 text-orange-500 flex-shrink-0" />
                              }
                              <span className="text-sm text-gray-700 flex-1 truncate">{f.name}</span>
                              <span className="text-[10px] text-gray-400 font-mono uppercase">{f.size > 1024*1024 ? `${(f.size/1024/1024).toFixed(1)} MB` : `${(f.size/1024).toFixed(0)} KB`}</span>
                              {(isPdf || isImg) && (
                                <button
                                  type="button"
                                  data-testid={`contractor-preview-file-${idx}`}
                                  onClick={() => { setPreviewIndex(idx); setPreviewOpen(true); }}
                                  className="text-orange-500 hover:text-orange-700"
                                  title={tr ? 'Önizle' : 'Preview'}
                                >
                                  <Eye className="h-4 w-4" />
                                </button>
                              )}
                              <button
                                type="button"
                                disabled={loading}
                                onClick={() => setAttachmentFiles(p => p.filter((_, i) => i !== idx))}
                                className="text-gray-400 hover:text-red-500 disabled:opacity-30"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                            {/* Upload progress bar (visible while submitting) */}
                            {typeof pct === 'number' && pct < 100 && (
                              <div className="mt-2 h-1 bg-orange-100 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-orange-500 transition-all duration-200"
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            )}
                            {pct === 100 && (
                              <div className="mt-1 text-[10px] text-emerald-600 font-semibold flex items-center gap-1">
                                <CheckCircle className="h-3 w-3" />
                                {tr ? 'Yüklendi' : 'Uploaded'}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {attachmentFiles.length < MAX_FILES && (
                        <button type="button" data-testid="contractor-browse-btn" onClick={() => fileInputRef.current?.click()}
                          className="w-full flex items-center justify-center gap-2 p-3 border-2 border-dashed border-orange-300 rounded-xl text-orange-600 hover:bg-orange-50 text-sm font-medium">
                          <Upload className="h-4 w-4" />
                          {tr ? `Dosya Seç (${attachmentFiles.length}/${MAX_FILES})` : `Browse Files (${attachmentFiles.length}/${MAX_FILES})`}
                        </button>
                      )}
                    </div>
                  )}

                  {/* Phase 86AZ.49 — About Us (optional, free text) */}
                  <div className="space-y-1.5 pt-2">
                    <Label className="text-sm font-semibold text-gray-700">
                      {tr ? 'Hakkımızda' : 'About Us'}
                    </Label>
                    <textarea
                      data-testid="contractor-about-us"
                      value={form.about_us}
                      onChange={e => set('about_us', e.target.value)}
                      rows={5}
                      maxLength={2000}
                      placeholder={tr
                        ? 'Firmanız, deneyiminiz, uzmanlık alanlarınız ve referanslarınız hakkında kısa bir tanıtım yazın…'
                        : 'Write a short introduction about your company, experience, expertise and references…'}
                      className="w-full text-sm border border-gray-200 rounded-xl p-3 bg-white focus:ring-2 focus:ring-orange-300 focus:outline-none leading-relaxed"
                    />
                    <p className="text-[10px] text-gray-400 text-right">{(form.about_us || '').length} / 2000</p>
                  </div>
                </>
              )}

              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl p-3">
                  <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                {step > 1 && (
                  <button type="button" onClick={prevStep}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-gray-200 text-gray-600 hover:border-orange-300 hover:text-orange-600 transition-all text-sm font-medium">
                    <ArrowLeft className="h-4 w-4" />
                    {tr ? 'Geri' : 'Back'}
                  </button>
                )}
                {step < 3 ? (
                  <Button data-testid={`contractor-step-${step}-next`} type="button" onClick={nextStep}
                    className="flex-1 bg-orange-500 hover:bg-orange-600 text-white rounded-xl py-5 font-semibold text-sm flex items-center justify-center gap-2">
                    {tr ? 'Devam Et' : 'Continue'}<ArrowRight className="h-4 w-4" />
                  </Button>
                ) : (
                  <Button data-testid="contractor-submit" type="button" onClick={handleSubmit} disabled={loading}
                    className="flex-1 bg-orange-500 hover:bg-orange-600 text-white rounded-xl py-5 font-semibold text-sm">
                    {loading
                      ? <span className="flex items-center justify-center gap-2">
                          <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                          {tr ? 'Gönderiliyor...' : 'Submitting...'}
                        </span>
                      : (tr ? 'Başvuruyu Gönder' : 'Submit Application')}
                  </Button>
                )}
              </div>
              {step === 1 && (
                <p className="text-center text-xs text-gray-400">
                  {tr ? 'Zaten hesabınız var mı?' : 'Have an account?'}{' '}
                  <button type="button" onClick={() => attemptExit()} className="text-orange-600 font-medium hover:underline">
                    {tr ? 'Giriş Yap' : 'Login'}
                  </button>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Phase 86AZ.24 — File preview carousel (PDF.js + image).
          `key` forces a fresh mount whenever a new file is targeted so
          the internal `idx` state starts at the requested initialIndex. */}
      <FilePreviewCarousel
        key={previewOpen ? `open-${previewIndex}-${attachmentFiles.length}` : 'closed'}
        files={attachmentFiles}
        initialIndex={previewIndex}
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        tr={tr}
      />

      {/* Phase 86AZ.51 — Exit-confirm modal. Triggered by the top-left Back
          button OR a browser/touchpad back gesture while the form is dirty. */}
      {confirmExitOpen && (
        <div
          className="fixed inset-0 z-[10001] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setConfirmExitOpen(false); }}
          data-testid="contractor-exit-confirm-modal"
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 bg-amber-50 flex items-center gap-2 text-amber-700">
              <AlertCircle className="h-5 w-5" />
              <h3 className="font-bold text-base">
                {tr ? 'Formdan çıkmak istediğinize emin misiniz?' : 'Are you sure you want to leave?'}
              </h3>
            </div>
            <div className="p-5 text-sm text-gray-700 leading-relaxed">
              {tr
                ? 'Doldurmaya başladığınız bilgiler ve yüklediğiniz dosyalar kaydedilmedi. Şu an çıkarsanız tüm girilen veriler kaybolacaktır.'
                : 'The information you started filling in and the files you uploaded have not been saved. If you leave now, all entered data will be lost.'}
            </div>
            <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex justify-end gap-2">
              <button
                type="button"
                data-testid="contractor-exit-cancel"
                onClick={() => setConfirmExitOpen(false)}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-100"
              >
                {tr ? 'Formda Kal' : 'Stay on Form'}
              </button>
              <button
                type="button"
                data-testid="contractor-exit-confirm"
                onClick={confirmExit}
                className="px-4 py-2 rounded-lg text-sm font-bold bg-red-600 hover:bg-red-700 text-white"
              >
                {tr ? 'Evet, Çık' : 'Yes, Leave'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
